import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Input,
  Select,
  SearchableSelect,
  DateRangePicker,
  Card,
  Badge,
  getStatusBadgeVariant,
  Pagination,
  EntityLink,
  CompactFilterBar,
  InsurerLogo,
  Table,
} from '@/components/common';
import { PolicyNumber, policyNumberLabel } from '@/components/common/PolicyNumber';
import type { Column, SortDirection } from '@/components/common/Table';
import { useCan } from '@/hooks/useEmployeeRights';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { useInsuranceTypesLookup, useProductsLookup } from '@/hooks/useProducts';
import { useCustomerPolicyFilterOptions } from '@/hooks/usePolicies';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams, type FilterDateRange } from '@/hooks/useFilterParams';
import { useAuthStore } from '@/store';
import { formatDate, formatCurrency } from '@/utils';
import { policyService, getErrorMessage } from '@/services';
import toast from 'react-hot-toast';
import { usePolicyList, type PolicyListFilters } from './policyListQuery';
import { LIFECYCLE_FILTER_OPTIONS, lifecycleBadgeLabel, type Policy } from '@/types';
import { isInsurerRole } from '@/routing/portalHome';

// B9 (c): the primary Status filter is the computed LIFECYCLE -- the same
// value the row badge shows -- never the raw stored `status` (in-force
// policies are stored as 'issued', so raw "Active" returned almost nothing)
// and no longer the old Active/Inactive label, which lumped Expired and
// Cancelled together. Sent as `?lifecycle_status=`.
const lifecycleStatusOptions = LIFECYCLE_FILTER_OPTIONS;
const LIFECYCLE_VALUES = new Set<string>(LIFECYCLE_FILTER_OPTIONS.map((o) => o.value).filter(Boolean));

// Board #281: the machine `reason` each skipped policy comes back with, in
// words an operator can act on. An unknown reason falls through to its raw
// value rather than being hidden -- a skip nobody can explain is still a skip
// they need to see.
const SKIP_REASON_LABELS: Record<string, string> = {
  not_found: 'not found in your book',
  posp_payout_completed: 'agent already paid (locked)',
  no_rate: 'no applicable commission rate',
  // A cancelled policy's commission was withdrawn deliberately. Recalculating
  // would revive it and pay the agent for business that no longer exists, so
  // the server refuses; these two say so rather than reading as a failure.
  policy_cancelled: 'policy cancelled (commission withdrawn)',
  commission_withdrawn: 'commission already withdrawn',
  error: 'failed to recalculate',
};

// Board #279. Tri-state, not a checkbox: "only what I archived" and
// "everything" are different questions. The absent value is the server's
// default (live records only), so the common case sends no param at all.
const archivedFilterOptions = [
  { value: '', label: 'Active records' },
  { value: 'true', label: 'Archived only' },
  { value: 'all', label: 'All records' },
];

// Queue #225 removed the "Raw Status" and "Payment Status" controls from this
// bar. Both stayed on the wire as `PolicyFilter` params (deep links and the
// broker insurer subtab still use them) -- what went is the two dropdowns, which
// duplicated information the row badges already carry: Status/Payment Status are
// rendered per row, and the primary Status filter above covers the case users
// actually reach for.

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Flat strings (plus one `{from,to}` range) because that is what
 * `useFilterParams` can decode from a URL; `toQueryFilters` below turns them
 * back into the `PolicyListFilters` the API speaks (`is_renewal` is a boolean
 * on the wire, the range is two separate params, and an empty string means
 * "send nothing" rather than "send blank").
 *
 * DELIBERATELY NOT HERE:
 *  - the page number. See `PAGE_IS_NOT_PERSISTED` below.
 *  - `posp` / `customer` / `created_from` / `created_to`. Those are one-shot
 *    deep-link SCOPING, not controls the user can reach -- they live in
 *    `scope` below so that writing this page's own filters back to the URL can
 *    never turn a one-shot scope into a permanent one.
 */
type PolicyPanelFilters = {
  search: string;
  lifecycle_status: string;
  insurer: string;
  insurance_type: string;
  product: string;
  commission_status: string;
  posp_assigned: string;
  is_renewal: string;
  archived: string;
  /** Stored as `f_issue_date_from` / `f_issue_date_to`. */
  issue_date: FilterDateRange;
  /** U-04: the server ordering field, '' when the list is in its default order. */
  sort: string;
  sort_dir: string;
};

const EMPTY_FILTERS: PolicyPanelFilters = {
  search: '',
  lifecycle_status: '',
  insurer: '',
  insurance_type: '',
  product: '',
  commission_status: '',
  posp_assigned: '',
  is_renewal: '',
  archived: '',
  issue_date: {},
  sort: '',
  sort_dir: '',
};

/**
 * Q-01 note 3: this page reads ~ten UNPREFIXED deep-link params, so its own
 * filters have to live in a separate namespace. Without this, picking an
 * insurer from the dropdown would write `?insurer=` -- the same param the
 * "arrived from a deep link" banner keys off -- and the banner would go
 * permanently live the moment anyone touched a control.
 */
const FILTER_PREFIX = 'f_';

/**
 * Every UNPREFIXED param this page treats as a ONE-SHOT deep-link seed.
 *
 * `useFilterParams` owns only the `f_` namespace (`writeFilterParams` starts
 * from the current query string and touches nothing but `${prefix}${key}`), so
 * neither `clearAll()` nor a filter the user empties can remove any of these.
 * Left in place they outrank the user on the NEXT mount, because `initial`
 * below re-seeds from them and an empty `f_` value is deleted rather than
 * written -- the namespace has no way to say "deliberately empty". Two bugs
 * fell out of that:
 *
 *   - Clear Filters was undone by a refresh. /policies?created_from=…&created_to=…
 *     (the dashboard "Policies Added Today" tile) -> add a filter -> Clear:
 *     the list correctly went unscoped but the URL still carried the window,
 *     so a reload/bookmark re-applied it.
 *   - A deep-linked filter the user cleared by hand came back. /policies?insurer=ins-1
 *     -> "All Insurance Companies" -> reload re-seeded ins-1 and the scoped
 *     banner returned.
 *
 * The fix is to CONSUME the seed: once the user has overridden a deep-linked
 * key (or cleared everything), the unprefixed param that seeded it is deleted
 * from the URL. It has done its one job. That also keeps the banner honest --
 * it means "arrived from a deep link AND it is still what is applied".
 */
const DEEP_LINK_PARAMS = [
  'insurer',
  'posp',
  'customer',
  'product',
  'insurance_type',
  'lifecycle_status',
  'issue_date_from',
  'issue_date_to',
  'created_from',
  'created_to',
] as const;

/**
 * Which unprefixed seed param(s) a given PANEL filter overrides. `posp`,
 * `customer` and `created_*` are absent on purpose: they are scope the user
 * cannot reach from a control here, so only Clear consumes them.
 */
const SEED_PARAMS_FOR: Partial<Record<keyof PolicyPanelFilters, readonly string[]>> = {
  insurer: ['insurer'],
  product: ['product'],
  insurance_type: ['insurance_type'],
  lifecycle_status: ['lifecycle_status'],
  issue_date: ['issue_date_from', 'issue_date_to'],
};

/**
 * U-04: the ONLY fields `PolicyViewSet.ordering_fields` accepts
 * (backend/apps/policies/views.py). A sortable header whose field the server
 * cannot order by is a lie -- it renders as a control, the click goes out, and
 * DRF's OrderingFilter silently drops the term, so the list comes back in
 * exactly the order it was already in. This project has shipped that bug
 * before, so the set is written out here and only columns in it are marked
 * `sortable`.
 *
 * Of the ten columns this page shows, only two are in it. `holder_name`,
 * `product__name`, `insurer_policy_number` and the computed lifecycle are NOT
 * orderable server-side; making those headers sortable needs a backend change
 * first, not a frontend flag.
 */
const SORTABLE_FIELDS = new Set(['total_premium', 'expiry_date']);

/**
 * PAGE_IS_NOT_PERSISTED.
 *
 * The page number is deliberately kept out of the URL. Every filter change on
 * this page resets it to 1, and the debounced-search effect below re-asserts
 * that on mount -- so a page number restored from the query string would be
 * overwritten before the first paint, leaving a URL that says `page=3` over a
 * list showing page 1. A restored number that an effect then resets is worse
 * than not restoring it. The user lands on page 1 of their restored filters,
 * which is the honest outcome, and the "filters survive a refresh" test below
 * pins it.
 */

export function PoliciesListPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // C5/C6: the customer role gets scoped filter options (this page must never
  // offer the platform-wide catalogue to a customer) and a narrower filter
  // bar (C6 drops Policy Date's "Today"/raw picker and Commission Status --
  // see the CompactFilterBar children below).
  const isCustomer = user?.role === 'customer';
  // #409: an insurer login reads only its own insurer's book, so the filter
  // bar must not list competitor insurers (the platform-wide lookup did) nor
  // offer broker-only filters the backend ignores for it
  // (`INSURER_POLICY_FILTERS`): Insurance Company, Commission Status, Agent
  // (POSP), Business Type and Archived are hidden; Status, Insurance Type,
  // Product and Policy Date stay, with options from its own scoped book.
  const isInsurer = isInsurerRole(user?.role);
  const usesScopedFilterOptions = isCustomer || isInsurer;
  // Broker Add Policy is not available to country_admin (item 4); for a gated
  // role it additionally requires policies Create (B4) -- `useCan` passes
  // through unaffected for every other role, so this only tightens. Call the
  // hooks unconditionally (Rules of Hooks) and combine afterwards.
  const hasAddPolicyRight = useCan('policies', 'create');
  const hasEditPolicyRight = useCan('policies', 'edit');
  const hasCreateQuoteRight = useCan('quotes', 'create');
  // POSP-2 / CUSTOMER-8: policies are issued by the broker. A POSP sells and a
  // customer buys, but neither creates a policy record, so "+New Policy" is
  // hidden for both. The server-side gate in PolicyViewSet.get_permissions is
  // what actually enforces it -- this only removes the affordance.
  // #351: nor does an insurer -- "New Policy" gave them Access Denied.
  const canAddPolicy =
    user?.role !== 'country_admin' &&
    user?.role !== 'posp' &&
    user?.role !== 'super_posp' &&
    user?.role !== 'customer' &&
    !isInsurerRole(user?.role) &&
    hasAddPolicyRight;
  // Board #279 / #281: who may run the bulk tools.
  //
  // The two are deliberately NOT the same set. Archiving is a policy WRITE and
  // `PolicyWritesAreBrokerSide` denies a country admin every one of those;
  // bulk recalculation is a back-office tool gated by `IsBrokerEmployee`,
  // which a country admin passes -- they are the ones asked to repair a
  // misconfigured book. Both are enforced server-side; this only decides
  // whether the button is offered.
  const canArchivePolicies =
    (user?.role === 'super_admin' || user?.role === 'broker_admin' || user?.role === 'broker_employee') &&
    hasEditPolicyRight;
  // B4: bulk recalculation moves money, so `bulk_recalculate_commission` is
  // ADMIN_ONLY on the server -- a broker employee no longer gets the button.
  const canRecalculateCommission =
    user?.role === 'super_admin' || user?.role === 'country_admin' || user?.role === 'broker_admin';
  // U-02/#279: the selection column only exists for a viewer who has at least
  // one bulk tool. A POSP or customer got a column of checkboxes wired to a
  // bar with nothing in it but "Clear selection".
  const canBulkSelect = canArchivePolicies || canRecalculateCommission;
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  // Honour entity-scoped deep links: /policies?insurer=… (My Policy for an
  // insurance company), ?posp=… (a POSP's policies), ?customer=… (a contact's
  // policies). These come from EntityLink / "My Policy" quick actions.
  const [searchParams, setSearchParams] = useSearchParams();
  const scopedInsurer = searchParams.get('insurer') || undefined;
  const scopedPosp = searchParams.get('posp') || undefined;
  const scopedCustomer = searchParams.get('customer') || undefined;

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link. The UNPREFIXED deep-link params
  // (queue #216's created_at window, #229's issue-date window, ?insurer=,
  // ?lifecycle_status=) are read ONCE here, in the hook's lazy initializer,
  // and merged UNDER anything already in the `f_` namespace -- so a value the
  // user actually chose always beats the deep-link seed, and the seed itself
  // is never written back to the query string.
  const { filters, setFilter, setFilters, clearAll } = useFilterParams<PolicyPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
    initial: (params) => {
      // B9: `?lifecycle_status=expired` deep links open on that slice. An
      // unknown value is dropped rather than sent, because the server 400s.
      const rawLifecycle = params.get('lifecycle_status') || '';
      const issueFrom = params.get('issue_date_from') || '';
      const issueTo = params.get('issue_date_to') || '';
      const issueDate: FilterDateRange = {};
      if (issueFrom) issueDate.from = issueFrom;
      if (issueTo) issueDate.to = issueTo;
      return {
        lifecycle_status: LIFECYCLE_VALUES.has(rawLifecycle) ? rawLifecycle : '',
        insurer: params.get('insurer') || '',
        product: params.get('product') || '',
        insurance_type: params.get('insurance_type') || '',
        issue_date: issueDate,
      };
    },
  });

  // The deep-link SCOPE, as opposed to the filters above: params the user has
  // no control over on this page, so they are read once and never mirrored
  // back. Held in state rather than read from the URL on every render so that
  // Clear can drop them, exactly as it did when they lived in `filters`.
  const [scope, setScope] = useState(() => ({
    posp: searchParams.get('posp') || '',
    customer: searchParams.get('customer') || '',
    // Queue #216: the dashboard's "Policies Added Today" tile links here with
    // a created_at window -- a different question from the issue-date window.
    created_from: searchParams.get('created_from') || '',
    created_to: searchParams.get('created_to') || '',
  }));

  // BROKER-9: search-as-you-type. `term` drives the input synchronously;
  // `debouncedTerm` (300ms) feeds the query so we don't fire a request per
  // keystroke. Seeded from the restored `?f_search=` so a refresh brings the
  // box back filled in, not just the results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // U-04: only a field the server can order by, so a stale/hand-edited
  // `?f_sort=` cannot leave a header drawn as sorted over a list that is not.
  const sortKey = SORTABLE_FIELDS.has(filters.sort) ? filters.sort : '';
  const sortDirection: SortDirection = filters.sort_dir === 'desc' ? 'desc' : 'asc';
  const ordering = sortKey ? `${sortDirection === 'desc' ? '-' : ''}${sortKey}` : undefined;

  const hasWindow = Boolean(filters.issue_date.from || filters.issue_date.to);

  // The wire shape. Memoized because it is the react-query key: rebuilding it
  // every render would refetch the list on every keystroke elsewhere.
  const queryFilters = useMemo<PolicyListFilters>(
    () => ({
      search: filters.search || undefined,
      lifecycle_status: filters.lifecycle_status || undefined,
      insurer: filters.insurer || undefined,
      insurance_type: filters.insurance_type || undefined,
      product: filters.product || undefined,
      commission_status: (filters.commission_status || undefined) as PolicyListFilters['commission_status'],
      posp_assigned: (filters.posp_assigned || undefined) as PolicyListFilters['posp_assigned'],
      is_renewal: filters.is_renewal === '' ? undefined : filters.is_renewal === 'true',
      archived: (filters.archived || undefined) as PolicyListFilters['archived'],
      issue_date_from: filters.issue_date.from || undefined,
      issue_date_to: filters.issue_date.to || undefined,
      posp: scope.posp || undefined,
      customer: scope.customer || undefined,
      created_from: scope.created_from || undefined,
      created_to: scope.created_to || undefined,
      ordering,
    }),
    [filters, scope, ordering]
  );

  const { data, isLoading, isError, refetch } = usePolicyList(page, queryFilters);
  // Memoized because `insurerOptions` below depends on it: `data?.results ?? []`
  // is a fresh array identity on every render while the list is empty.
  const policies = useMemo(() => data?.results ?? [], [data]);
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // ── Bulk selection (shared by #279 archive and #281 recalculate) ─────────
  // Held as ids rather than rows so the selection survives a refetch, and
  // cleared whenever the visible set changes -- acting on a row the user can
  // no longer see is exactly the kind of silent mistake these tools must not
  // make.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => {
    setSelectedIds([]);
  }, [page, queryFilters]);
  const visibleIds = useMemo(() => policies.map((policy) => policy.id), [policies]);
  const selectedOnPage = useMemo(
    () => selectedIds.filter((id) => visibleIds.includes(id)),
    [selectedIds, visibleIds]
  );
  const allOnPageSelected = visibleIds.length > 0 && selectedOnPage.length === visibleIds.length;
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };
  const toggleAllOnPage = () => {
    setSelectedIds((prev) => (allOnPageSelected ? prev.filter((id) => !visibleIds.includes(id)) : [
      ...prev,
      ...visibleIds.filter((id) => !prev.includes(id)),
    ]));
  };

  const refreshList = () => queryClient.invalidateQueries({ queryKey: ['policies'] });

  const handleBulkArchive = async (archived: boolean) => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const result = await policyService.bulkArchivePolicies(selectedIds, archived);
      // Report what the SERVER did, not what was asked for: an id outside the
      // caller's book comes back under not_found and was never touched.
      const verb = archived ? 'archived' : 'restored';
      const parts = [`${result.updated.length} ${verb}`];
      if (result.unchanged.length) parts.push(`${result.unchanged.length} already ${verb}`);
      if (result.not_found.length) parts.push(`${result.not_found.length} not found`);
      toast.success(parts.join(' · '));
      setSelectedIds([]);
      await refreshList();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkRecalculate = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const result = await policyService.bulkRecalculateCommission(selectedIds);
      if (result.recalculated.length) {
        toast.success(`Commission recalculated for ${result.recalculated.length} policies.`);
      }
      // Every skip is named. A batch that silently moved nothing while
      // returning 200 is this platform's signature failure, so each reason is
      // surfaced with its count rather than folded into "some were skipped".
      if (result.skipped.length) {
        const counts = new Map<string, number>();
        result.skipped.forEach((row) => counts.set(row.reason, (counts.get(row.reason) ?? 0) + 1));
        const summary = [...counts.entries()]
          .map(([reason, count]) => `${count} ${SKIP_REASON_LABELS[reason] ?? reason}`)
          .join(' · ');
        toast.error(`Skipped: ${summary}`, { duration: 6000 });
      }
      setSelectedIds([]);
      await refreshList();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  };

  // The deep-link banner tracks the filter that is ACTUALLY applied, not just
  // the URL: the insurer is now also a normal dropdown, so a user who arrives
  // via ?insurer=… and then picks a different one (or clears it) must stop
  // being told the list is scoped to "the selected insurance company".
  //
  // Q-01: it still means "arrived here from a deep link", never "has filters".
  // The comparison is against the UNPREFIXED param only -- a user-chosen
  // insurer lives in `?f_insurer=` and can never raise this banner.
  const isScoped = Boolean(
    (scopedInsurer && filters.insurer === scopedInsurer) ||
      (scopedPosp && scope.posp === scopedPosp) ||
      (scopedCustomer && scope.customer === scopedCustomer)
  );

  // C5: for a customer, all three filter dropdowns below must offer only
  // values present on THEIR OWN policies -- the platform-wide lookups leak
  // the whole catalogue otherwise (an insurer/type/product a customer never
  // bought, selected, silently returns zero results indistinguishable from
  // "no results for what you searched"). Scoped SERVER-SIDE
  // (`/policies/filter-options/`, `PolicyViewSet.filter_options`) rather than
  // derived from `policies` (this page's own paginated list state): that
  // would only ever see the CURRENT PAGE's values, silently dropping options
  // for policies that happened to fall on a later page. Every other role is
  // unaffected -- the three lookups below stay wired exactly as before.
  // #409: an insurer login uses the same scoped endpoint.
  const { data: customerFilterOptions } = useCustomerPolicyFilterOptions({ enabled: usesScopedFilterOptions });

  // Insurer filter options. `lookup` returns ACTIVE insurers only, so a deep
  // link to a paused/blocked insurer would otherwise leave the control reading
  // "All Insurance Companies" while the list stayed filtered — keep whatever is
  // actually applied in the list, named from the rows when we can.
  const { data: insurerLookup } = useInsurersLookup({ enabled: !usesScopedFilterOptions });
  const insurerOptions = useMemo(() => {
    const options = usesScopedFilterOptions
      ? (customerFilterOptions?.insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name }))
      : (insurerLookup ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name }));
    const applied = filters.insurer;
    if (applied && !options.some((option) => option.value === applied)) {
      const nameFromRows = policies.find((policy) => policy.insurer === applied)?.insurer_name;
      options.unshift({ value: applied, label: nameFromRows || 'Selected insurance company' });
    }
    return [{ value: '', label: 'All Insurance Companies' }, ...options];
  }, [usesScopedFilterOptions, customerFilterOptions, insurerLookup, filters.insurer, policies]);

  // Queue #226: Insurance Type and Product filter sources. The product list
  // narrows to the chosen insurance type / insurer when either is set, and
  // otherwise offers the whole catalogue -- `enabled: true` is what opts this
  // caller out of useProductsLookup's "wait for a selection" default. Both of
  // these platform-wide lookups are replaced by `customerFilterOptions` above
  // for a customer -- see the C5 comment there.
  const { data: insuranceTypeLookup } = useInsuranceTypesLookup({ enabled: !usesScopedFilterOptions });
  // Both lookups return a bare array today. Guarded with Array.isArray anyway:
  // this runs inside the Policies page's render, so anything else on the wire
  // (an error envelope, a paginated response after a future endpoint change)
  // would not degrade the filter -- it would throw during render and take the
  // whole policy list down with it.
  const insuranceTypeOptions = useMemo(() => {
    const source = usesScopedFilterOptions
      ? customerFilterOptions?.insurance_types ?? []
      : Array.isArray(insuranceTypeLookup)
        ? insuranceTypeLookup
        : [];
    return [
      { value: '', label: 'All Insurance Types' },
      ...source.map((type) => ({ value: type.id, label: type.name })),
    ];
  }, [usesScopedFilterOptions, customerFilterOptions, insuranceTypeLookup]);
  const { data: productLookup } = useProductsLookup(
    {
      insurance_type: filters.insurance_type || undefined,
      insurer: filters.insurer || undefined,
    },
    // C5: a customer's product options come from the scoped endpoint above,
    // not this platform-wide lookup -- skip firing the request for that role.
    { enabled: !usesScopedFilterOptions }
  );
  const productOptions = useMemo(() => {
    const source = usesScopedFilterOptions
      ? customerFilterOptions?.products ?? []
      : Array.isArray(productLookup)
        ? productLookup
        : [];
    return [
      { value: '', label: 'All Products' },
      ...source.map((product) => ({ value: product.id, label: product.name })),
    ];
  }, [usesScopedFilterOptions, customerFilterOptions, productLookup]);

  /**
   * Delete the named UNPREFIXED deep-link params from the URL -- see
   * `DEEP_LINK_PARAMS`. Called whenever the user overrides a key one of them
   * seeded, so the seed cannot come back and outrank them on the next mount.
   *
   * Two writers in one tick is safe here, and deliberately so: `useFilterParams`
   * defers ITS write to a layout effect and issues it with the functional form
   * against the query string react-router has actually committed, precisely for
   * this co-tenant case. So this navigation lands first and the `f_*` write is
   * computed on top of it, whichever order the calls are made in.
   *
   * No-op when none of them is present, so an ordinary filter change on an
   * ordinary /policies URL costs no extra navigation.
   */
  const consumeSeedParams = (names: readonly string[]) => {
    if (!names.some((name) => searchParams.has(name))) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const name of names) next.delete(name);
        return next;
      },
      { replace: true }
    );
  };

  // Changing the type re-narrows the product list, so a product left selected
  // from the previous type would silently keep filtering the list to a product
  // no longer offered in the dropdown -- an empty page with no visible cause.
  const handleInsuranceTypeChange = (value: string) => {
    setFilters({ insurance_type: value, product: '' });
    // Both keys are user-chosen from here on.
    consumeSeedParams([...(SEED_PARAMS_FOR.insurance_type ?? []), ...(SEED_PARAMS_FOR.product ?? [])]);
    setPage(1);
  };

  // Push the debounced term into the filters and reset to the first page
  // whenever the effective search changes.
  //
  // Keyed off a ref of the LAST TERM PUSHED, not off `filters.search`. Seeded
  // with the restored value, it makes the mount pass a no-op (the box and the
  // filter already agree, so no second request goes out); and because Clear
  // resets `filters.search` while `debouncedTerm` still holds the old text for
  // another 300ms, comparing against `filters.search` here would immediately
  // re-apply the search the user just cleared.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilter('search', debouncedTerm);
    setPage(1);
  }, [debouncedTerm, setFilter]);

  const handleFilterChange = (key: keyof PolicyPanelFilters, value: string) => {
    setFilters({ [key]: value } as Partial<PolicyPanelFilters>);
    consumeSeedParams(SEED_PARAMS_FOR[key] ?? []);
    setPage(1);
  };

  // U-04: server-side sort. `Table` never reorders the rows it is given -- it
  // reports the next sort state and this sends it as DRF's `?ordering=`, so
  // the order is the whole filtered book's, not this page of twenty.
  const handleSortChange = (key: string, direction: SortDirection) => {
    setFilters({ sort: key, sort_dir: direction });
    setPage(1);
  };

  // Queue #214: which shape of date window the user picked. Derived from the
  // filters rather than held as the source of truth, so a `?issue_date_from=`
  // deep link (or a restored `?f_issue_date_from=`) still opens showing the
  // custom range it describes instead of reading as "All dates" while the list
  // is plainly filtered.
  const [dateModeOverride, setDateModeOverride] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const isToday = filters.issue_date.from === today && filters.issue_date.to === today;
  const dateMode = dateModeOverride ?? (isToday ? 'today' : hasWindow ? 'custom' : '');

  const handleDateModeChange = (mode: string) => {
    setDateModeOverride(mode || null);
    if (mode === 'today') {
      setFilter('issue_date', { from: today, to: today });
    } else if (mode === '') {
      setFilter('issue_date', {});
    }
    // 'custom' leaves whatever is set alone and reveals the two bounds, so
    // switching Today -> Custom keeps today's window as the starting point
    // rather than blanking what the user just chose.
    if (mode !== 'custom') consumeSeedParams(SEED_PARAMS_FOR.issue_date ?? []);
    setPage(1);
  };

  const handleIssueDateBoundChange = (bound: 'from' | 'to', value: string) => {
    const next: FilterDateRange = { ...filters.issue_date };
    if (value) next[bound] = value;
    else delete next[bound];
    setFilter('issue_date', next);
    consumeSeedParams(SEED_PARAMS_FOR.issue_date ?? []);
    setPage(1);
  };

  // C6: the customer role's calendar range picker sets both bounds from one
  // control (no separate "Policy Date" mode selector exists for that role --
  // see the filter bar below), in a single filters update rather than two
  // changes, so the two bounds land in the same render/request instead of
  // briefly disagreeing.
  const handleIssueDateRangeChange = (from: string, to: string) => {
    const next: FilterDateRange = {};
    if (from) next.from = from;
    if (to) next.to = to;
    setFilter('issue_date', next);
    consumeSeedParams(SEED_PARAMS_FOR.issue_date ?? []);
    setPage(1);
  };

  // Task 10: count of the PANEL's own filters (search is always visible, so
  // it is excluded; posp/customer are URL-scoping, not panel controls, and
  // are already surfaced by the "isScoped" banner above; the sort is a view
  // preference, not a filter, so it does not badge either). Badges the Filter
  // toggle and decides whether the panel starts expanded.
  const activeFilterCount = [
    filters.lifecycle_status,
    filters.insurance_type,
    filters.insurer,
    filters.product,
    filters.commission_status,
    // Board #254. NOT `scope.posp` -- that is the ?posp=<id> deep-link scope
    // surfaced by the isScoped banner above, not a panel control.
    filters.posp_assigned,
    // Queue #214: the window is one filter the user applied, however many
    // bounds it puts on the wire. Counting both made "Today" badge as 2.
    hasWindow ? 'window' : '',
    filters.is_renewal,
    // Board #279: "Archived only" / "All records" narrows or widens what the
    // page shows, so it has to badge like every other applied filter.
    filters.archived,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    // Q-01: this is the one action that empties the `?f_*` namespace. It also
    // resets the sort, which is in that namespace so a shared link reproduces
    // the view the sender was looking at.
    clearAll();
    // ...and this is what makes Clear survive a REFRESH. `clearAll` empties
    // the `f_` namespace only; every unprefixed deep-link param would
    // otherwise sit untouched in the URL and re-seed the filters (and the
    // scope, and the banner) the moment the page is reloaded or the link
    // bookmarked. Q-01's acceptance is "preserved across a refresh UNTIL
    // Clear Filters", so Clear has to empty both.
    consumeSeedParams(DEEP_LINK_PARAMS);
    setScope({ posp: '', customer: '', created_from: '', created_to: '' });
    setDateModeOverride(null);
    setSelectedIds([]);
    setPage(1);
  };

  const handleRowClick = (policy: Policy) => {
    navigate(`/policies/${policy.id}`);
  };

  // Queue #235 inline actions.
  //
  // Renew mirrors the policy detail page's "Create Renewal Quote": the button
  // opens /quotes/new, so it may only be drawn for a viewer that route admits.
  //
  // W4m: this was a DENYLIST (`!== 'customer' && !== 'country_admin'`), which
  // named the two roles someone had hit and left `insurer_admin` and
  // `insurer_employee` -- both of which reach this list -- a button that only
  // lands on /unauthorized. An allowlist copied from the route cannot drift
  // that way: a role added to one side has to be added here deliberately.
  // POSPs keep it -- POSP quote creation was deliberately restored on
  // 2026-07-25 (POSP-5). `hasCreateQuoteRight` is the route's
  // `requires={{ module: 'quotes', level: 'create' }}` (B4).
  const QUOTE_CREATE_ROLES = ['broker_admin', 'broker_employee', 'posp', 'super_posp'];
  const canRenew = QUOTE_CREATE_ROLES.includes(user?.role ?? '') && hasCreateQuoteRight;

  // Which policy's document is being fetched, so only that row spins.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // The list payload does not carry documents, and pre-fetching them for every
  // visible row would be 20 extra requests per page for a button most rows
  // never get clicked. Resolve on click instead: one request for the document
  // list, then one for the file.
  const handleDownload = async (policy: Policy) => {
    setDownloadingId(policy.id);
    try {
      const docs = await policyService.getPolicyDocuments(policy.id);
      const results = docs.results ?? [];
      if (results.length === 0) {
        toast.error('No document uploaded for this policy yet.');
        return;
      }
      const doc = results.find((d) => d.document_type === 'policy_copy') ?? results[0];
      const blob = await policyService.downloadPolicyDocument(policy.id, doc.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name || `${policy.policy_number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  };

  /**
   * U-02: the columns, with a width each.
   *
   * `layout="fixed"` without widths collapses every column to an equal share,
   * which is worse than what was here before -- so each one is sized for the
   * content it actually holds, and the widths are what stop the columns
   * jumping about when a filter changes which rows are on screen.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` with `px-6`
   * (48px) and, under `layout="fixed"`, sets `truncate` -- i.e.
   * `overflow: hidden`. A column declared at 7rem therefore offers 64px of
   * content, not 112px, and anything wider is CLIPPED. Four columns here were
   * sized as though the padding did not exist, which was invisible only for
   * as long as `table-fixed` was inert; the worst of them was Actions, whose
   * three 28px icon buttons (`p-1.5` + a 16px icon) plus two 4px gaps need
   * 92px against the 64px it declared -- the Renew button was clipped away
   * entirely, unclickable, with no scroll and no ellipsis to show it had gone.
   *
   * So the four tight columns override the padding to `px-3` (24px) through
   * `className` -- `cn` is tailwind-merge, so a `px-*` there beats the `px-6`,
   * exactly as the select column already did -- and take their width from the
   * arithmetic below. The four generous text columns give a little back, so
   * the total still fits:
   *
   *   select          3rem  px-3 ->  24px content (16px checkbox + focus ring)
   *   policy         13rem  px-6 -> 160px
   *   holder         10rem  px-6 -> 112px
   *   product        10rem  px-6 -> 112px
   *   posp           10rem  px-6 -> 112px
   *   vehicle         9rem  px-6 ->  96px
   *   total_premium   8.5rem px-3 -> 112px (formatCurrency of a crore: ~95px)
   *   status          9rem  px-3 -> 120px
   *   expiry_date     8rem  px-3 -> 104px ("Sep 20, 2026": ~85px)
   *   actions         8rem  px-3 -> 104px (three icon buttons: 92px)
   *                 --------
   *                  88.5rem = 1416px, inside the 1424px that was measured as
   *   the widest this page can be and still fit a 1440-1536px desktop with no
   *   horizontal scrollbar. Narrower than that (1280px, and every phone) the
   *   table -- not the page -- scrolls sideways inside its own card.
   *
   * THE THREE CELLS THAT COULD ELLIPSISE SILENTLY now carry their own
   * `title`. `Table` sets a tooltip only when a cell renders to a string or a
   * number (stringifying arbitrary JSX would show "[object Object]"), and
   * premium, expiry and status all return JSX -- so money and dates would have
   * been cut off with nothing to hover. The Status badge additionally gets
   * `max-w-full` with the label in a `truncate` span: an `inline-flex` badge is
   * an atomic inline-level box, which a `truncate` ancestor hard-CLIPS with no
   * ellipsis, and "Expired * in grace until Sep 20, 2026" is far wider than any
   * sane column.
   *
   * These widths are live: `Table` sets `w-full` alongside `table-fixed`
   * (recipe STEP 0), without which `table-layout: fixed` is inert and the
   * browser re-derives every column from the rows on screen.
   */
  const columns: Column<Policy>[] = [
    ...(canBulkSelect
      ? [
          {
            key: 'select',
            header: '',
            width: '3rem',
            // 24px of content for a 16px checkbox -- `px-4` left exactly 16px,
            // which clipped the focus ring.
            className: 'px-3',
            render: (policy: Policy) => (
              // The row navigates on click, so the checkbox has to stop the
              // bubble or ticking a box would open the policy.
              <span className="inline-flex" onClick={(event) => event.stopPropagation()}>
                <input
                  type="checkbox"
                  aria-label={`Select policy ${policyNumberLabel(policy)}`}
                  checked={selectedIds.includes(policy.id)}
                  onChange={() => toggleOne(policy.id)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                />
              </span>
            ),
          } as Column<Policy>,
        ]
      : []),
    {
      key: 'policy',
      header: 'Policy',
      width: '13rem',
      render: (policy: Policy) => (
        <div>
          {/* Show broker-entered policy number; fall back to internal ref only when blank */}
          <p
            className="truncate text-sm font-medium text-primary-600 dark:text-primary-400"
            title={policyNumberLabel(policy)}
          >
            <PolicyNumber source={policy} />
          </p>
          {/* U-03: the logo goes INSIDE this cell, beside the insurer name it
              already carried, rather than taking a column of its own -- the
              widths above are arithmetic against a 1424px page and a tenth
              column would not fit. `xs` (20px) is the mark at the size of the
              line it sits on; `min-w-0` lets the name truncate rather than
              pushing the picture out of the cell. */}
          <span className="mt-0.5 flex items-center gap-1.5">
            <InsurerLogo src={policy.insurer_logo_url} name={policy.insurer_name} size="xs" />
            <EntityLink
              kind="insurer"
              id={policy.insurer}
              name={policy.insurer_name}
              className="min-w-0 truncate text-xs text-gray-500 hover:underline dark:text-gray-400"
            />
          </span>
          {policy.is_archived && (
            <Badge variant="default" size="sm" className="mt-1">
              Archived
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'holder',
      header: 'Holder',
      width: '10rem',
      render: (policy: Policy) => (
        <div>
          <p className="truncate text-sm text-gray-900 dark:text-white" title={policy.holder_name}>
            {policy.holder_name}
          </p>
          {policy.holder_phone && (
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">{policy.holder_phone}</p>
          )}
        </div>
      ),
    },
    {
      key: 'product',
      header: 'Product',
      width: '10rem',
      render: (policy: Policy) => (
        <div>
          <p className="truncate text-sm text-gray-900 dark:text-white" title={policy.product_name}>
            {policy.product_name}
          </p>
          {policy.is_renewal && (
            <Badge variant="info" size="sm" className="mt-1">
              Renewal
            </Badge>
          )}
        </div>
      ),
    },
    {
      // Board #254
      key: 'posp',
      header: 'Agent (POSP)',
      width: '10rem',
      render: (policy: Policy) =>
        policy.posp_name ? (
          // The row itself navigates to the policy, and that handler fires on
          // the way up -- without stopping the bubble the agent link would open
          // the policy instead of the agent.
          <span className="block truncate" onClick={(event) => event.stopPropagation()}>
            <EntityLink
              kind="posp"
              id={policy.posp}
              name={policy.posp_name}
              className="text-sm text-primary-600 hover:underline dark:text-primary-400"
            />
          </span>
        ) : (
          // Was text-gray-400 on white: ~2.8:1, below the 4.5:1 floor for body
          // text. gray-500 carries the same "nothing here" weight and passes.
          <span className="text-sm text-gray-500 dark:text-gray-400">Not Assigned</span>
        ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      width: '9rem',
      render: (policy: Policy) =>
        policy.vehicle_number || policy.vehicle_type ? (
          <div>
            {policy.vehicle_number && (
              <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {policy.vehicle_number}
              </p>
            )}
            {policy.vehicle_type && (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={policy.vehicle_type}>
                {policy.vehicle_type}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">—</p>
        ),
    },
    {
      key: 'total_premium',
      header: 'Total Premium',
      width: '8.5rem',
      className: 'px-3',
      // U-04: `total_premium` IS in PolicyViewSet.ordering_fields.
      sortable: true,
      render: (policy: Policy) => (
        // `title` here, not from `Table`: this cell renders JSX, and `Table`
        // only tooltips string/number content -- so a premium too wide for the
        // column would ellipsise with no way to read it.
        <p
          className="truncate text-sm font-medium text-gray-900 dark:text-white"
          title={formatCurrency(policy.total_premium)}
        >
          {formatCurrency(policy.total_premium)}
        </p>
        /* The payment badge is gone: a policy exists only once payment has
           succeeded, so a paid/unpaid state printed next to every premium was a
           duplicate that could contradict the policy's own status. The column
           is untouched in the database. */
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '9rem',
      className: 'px-3',
      render: (policy: Policy) => {
        // B9 (c): computed lifecycle, with an "in grace" suffix on an Expired
        // policy still inside its grace period. A draft/pending policy has no
        // lifecycle and shows a dash, not a made-up status.
        const label = lifecycleBadgeLabel(policy);
        return (
          // `max-w-full` + a `truncate` SPAN inside: the badge is an
          // `inline-flex` atomic box, so a `truncate` on the cell around it
          // clips it dead rather than ellipsising. As a flex item the span is
          // blockified and can shrink below its content, which is the same
          // trick `Table` uses for a long sortable header.
          <Badge
            variant={getStatusBadgeVariant(policy.lifecycle_status ?? '')}
            size="sm"
            className="max-w-full"
          >
            <span className="truncate" title={label}>
              {label}
            </span>
          </Badge>
        );
      },
    },
    {
      key: 'expiry_date',
      header: 'Expiry',
      width: '8rem',
      className: 'px-3',
      // U-04: `expiry_date` IS in PolicyViewSet.ordering_fields.
      sortable: true,
      render: (policy: Policy) => (
        // See the premium cell: JSX content gets no tooltip from `Table`.
        <p
          className="truncate text-sm text-gray-500 dark:text-gray-400"
          title={formatDate(policy.expiry_date)}
        >
          {formatDate(policy.expiry_date)}
        </p>
      ),
    },
    {
      // Queue #235: the Actions column used to exist only for roles that can
      // mark payment received, and rendered "—" for every row once payment was
      // complete. It now always carries the inline quick actions.
      key: 'actions',
      header: 'Actions',
      // 104px of content for three 28px icon buttons and two 4px gaps (92px).
      // At the 7rem/px-6 this used to declare, the cell offered 64px and the
      // Renew button was clipped out of existence.
      width: '8rem',
      className: 'px-3',
      render: (policy: Policy) => (
        <div className="flex items-center gap-1" onClick={(event) => event.stopPropagation()}>
          <IconAction title="View policy" onClick={() => navigate(`/policies/${policy.id}`)}>
            <EyeIcon className="w-4 h-4" />
          </IconAction>
          <IconAction
            title="Download policy document"
            busy={downloadingId === policy.id}
            onClick={() => handleDownload(policy)}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
          </IconAction>
          {canRenew && (
            <IconAction
              title="Create renewal quote"
              onClick={() => navigate(`/quotes/new?renewal=${policy.id}`)}
            >
              <ArrowPathIcon className="w-4 h-4" />
            </IconAction>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Policies"
        subtitle={`${totalItems} total policies`}
        breadcrumbs={[{ label: 'Policies' }]}
        actions={
          canAddPolicy ? (
            <Link to="/policies/new">
              <Button leftIcon={<PlusIcon className="w-5 h-5" />}>New Policy</Button>
            </Link>
          ) : undefined
        }
      />

      {isScoped && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm dark:border-primary-800 dark:bg-primary-900/20">
          <span className="text-primary-800 dark:text-primary-200">
            Showing policies filtered to{' '}
            {scopedInsurer ? 'the selected insurance company' : scopedPosp ? 'the selected agent (POSP)' : 'the selected contact'}.
          </span>
          {/* Clearing the URL param alone left the filter state behind (the
              route element is not remounted), so the banner vanished while the
              list stayed scoped. Clear both. */}
          <Link
            to="/policies"
            onClick={clearFilters}
            className="font-medium text-primary-700 underline dark:text-primary-300"
          >
            Show all policies
          </Link>
        </div>
      )}

      {/* Task 10 (Broker 3): one line by default -- search box + Filter
          toggle badged with the active count. This used to be three lines
          with every filter always visible (itself a fix for an EARLIER
          disclosure-button design where an applied filter went invisible
          the moment the panel closed). CompactFilterBar's panel starts
          expanded whenever a filter is already set — the ?insurer= deep link,
          or a filter restored from ?f_* after a refresh — so that failure mode
          does not return. */}
      <CompactFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Policy number, holder name, phone, vehicle number..."
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        // Queue #213: six filters on one row from xl up. Below that they wrap
        // rather than shrink to unusable widths.
        columns={6}
      >
        <Select
          id="policy-filter-status"
          label="Status"
          options={lifecycleStatusOptions}
          value={filters.lifecycle_status}
          onChange={(e) => handleFilterChange('lifecycle_status', e.target.value)}
        />
        <SearchableSelect
          id="policy-filter-insurance-type"
          label="Insurance Type"
          options={insuranceTypeOptions}
          value={filters.insurance_type}
          onChange={handleInsuranceTypeChange}
          placeholder="All Insurance Types"
        />
        {/* #409: hidden for an insurer login -- its list is already its own
            insurer only, and the choice would only ever be itself. */}
        {!isInsurer && (
          <SearchableSelect
            id="policy-filter-insurer"
            label="Insurance Company"
            options={insurerOptions}
            value={filters.insurer}
            onChange={(value) => handleFilterChange('insurer', value)}
            placeholder="All Insurance Companies"
          />
        )}
        <SearchableSelect
          id="policy-filter-product"
          label="Product"
          options={productOptions}
          value={filters.product}
          onChange={(value) => handleFilterChange('product', value)}
          placeholder="All Products"
        />
        {/* Queue #214: one Policy Date control where there were two date
            boxes. The bounds are still `issue_date_from` / `issue_date_to` on
            the wire -- this is a change to how the window is chosen, not to
            what is sent, so the deep links and the backend filter are
            untouched. The backend falls back to inception_date for policies
            with no stored issue date, matching how the reports and the
            commission ledger date a policy.
            C6: dropped for the customer role -- a customer has no notion of
            "today's bookings" the way a broker's back office does, and the
            calendar range picker below covers the one case (a custom window)
            that control existed to unlock. Broker roles are unaffected. */}
        {!isCustomer && (
          <Select
            id="policy-filter-policy-date"
            label="Policy Date"
            options={[
              { value: '', label: 'All dates' },
              { value: 'today', label: 'Today' },
              { value: 'custom', label: 'Custom range' },
            ]}
            value={dateMode}
            onChange={(e) => handleDateModeChange(e.target.value)}
          />
        )}
        {isCustomer ? (
          // C6: a single interactive calendar in place of the two plain date
          // boxes below AND the "Policy Date" mode selector above -- there is
          // no mode to switch on any more, so the range picker is always
          // visible rather than gated behind `dateMode === 'custom'`. Same
          // wire fields (`issue_date_from`/`issue_date_to`), so this is a
          // presentation change only.
          <DateRangePicker
            id="policy-filter-issue-date-range"
            label="Issued Date"
            fromValue={filters.issue_date.from || ''}
            toValue={filters.issue_date.to || ''}
            onChange={handleIssueDateRangeChange}
            placeholder="All dates"
          />
        ) : (
          dateMode === 'custom' && (
            <>
              <Input
                id="policy-filter-issue-date-from"
                type="date"
                label="Issued From"
                value={filters.issue_date.from || ''}
                max={filters.issue_date.to || undefined}
                onChange={(e) => handleIssueDateBoundChange('from', e.target.value)}
              />
              <Input
                id="policy-filter-issue-date-to"
                type="date"
                label="Issued To"
                value={filters.issue_date.to || ''}
                min={filters.issue_date.from || undefined}
                onChange={(e) => handleIssueDateBoundChange('to', e.target.value)}
              />
            </>
          )
        )}
        {/* Queue #195: which policies have had commission calculated. The
            useful half is usually the second -- what is still owed one --
            which previously meant opening records one at a time.
            C6: dropped for the customer role -- commission is what the
            broker earns on the sale, not something a customer's own policy
            list has any business surfacing. Broker roles are unaffected. */}
        {!isCustomer && !isInsurer && (
          <Select
            id="policy-filter-commission-status"
            label="Commission Status"
            options={[
              { value: '', label: 'All Commission Status' },
              { value: 'calculated', label: 'Calculated' },
              { value: 'not_calculated', label: 'Not Calculated' },
            ]}
            value={filters.commission_status}
            onChange={(e) => handleFilterChange('commission_status', e.target.value)}
          />
        )}
        {/* #409: Agent (POSP), Business Type and Archived are broker
            back-office filters; the backend ignores the first two for an
            insurer login (`INSURER_POLICY_FILTERS`) and its dashboard never
            counts archived rows, so none of them is offered there. */}
        {!isInsurer && (
          <>
            {/* Board #254: "which policies have no agent on them?" -- the question
                a broker admin actually asks, which ?posp=<id> (one named agent)
                could not express. Independent of that param: both can be on the
                wire at once and the backend applies each as its own .filter(). */}
            <Select
              id="policy-filter-posp-assigned"
              label="Agent (POSP)"
              options={[
                { value: '', label: 'All Agents' },
                { value: 'assigned', label: 'Assigned' },
                { value: 'not_assigned', label: 'Not Assigned' },
              ]}
              value={filters.posp_assigned}
              onChange={(e) => handleFilterChange('posp_assigned', e.target.value)}
            />
            <Select
              id="policy-filter-business-type"
              label="Business Type"
              options={[
                { value: '', label: 'All Policies' },
                { value: 'true', label: 'Renewals Only' },
                { value: 'false', label: 'New Business Only' },
              ]}
              value={filters.is_renewal}
              onChange={(e) => handleFilterChange('is_renewal', e.target.value)}
            />
            {/* Board #279: archived policies are out of the list by default. This
                is the only way back to them -- there is no delete, so nothing is
                ever actually gone. */}
            <Select
              id="policy-filter-archived"
              label="Archived"
              options={archivedFilterOptions}
              value={filters.archived}
              onChange={(e) => handleFilterChange('archived', e.target.value)}
            />
          </>
        )}
      </CompactFilterBar>

      {/* Board #279 + #281: the selection bar. Only rendered when something is
          selected, so the page is unchanged for everyone who never uses it. */}
      {selectedIds.length > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 dark:border-primary-800 dark:bg-primary-900/20"
          data-testid="policy-bulk-bar"
        >
          <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
            {selectedIds.length} selected
          </span>
          {canRecalculateCommission && (
            <Button
              variant="outline"
              size="sm"
              isLoading={bulkBusy}
              leftIcon={<CalculatorIcon className="h-4 w-4" />}
              onClick={handleBulkRecalculate}
            >
              Recalculate Commission
            </Button>
          )}
          {canArchivePolicies && (
            <>
              <Button
                variant="outline"
                size="sm"
                isLoading={bulkBusy}
                leftIcon={<ArchiveBoxIcon className="h-4 w-4" />}
                onClick={() => handleBulkArchive(true)}
              >
                Archive
              </Button>
              <Button
                variant="outline"
                size="sm"
                isLoading={bulkBusy}
                leftIcon={<ArrowUturnLeftIcon className="h-4 w-4" />}
                onClick={() => handleBulkArchive(false)}
              >
                Restore
              </Button>
            </>
          )}
          <button
            type="button"
            className="text-sm font-medium text-primary-700 underline dark:text-primary-300"
            onClick={() => setSelectedIds([])}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* A REFRESH that failed while the rows it was replacing are still in
          hand -- a refetch of the same query, which react-query leaves showing
          its previous data. That is what happens after a bulk archive or
          recalculation invalidates the list and the reload then fails: the
          toast says the action worked, the rows on screen are the ones from
          before it, and nothing says the two disagree. (A filter CHANGE is a
          different query key, and a failure there drops to the full error
          branch below -- so this never hides an unapplied filter.) */}
      {isError && policies.length > 0 && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>Could not refresh this list. These are the results from before your last change.</span>
          <button type="button" className="font-medium underline" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {/* Policies Table.
          Three states, never two: a failed request, an empty book and a
          loading list are different facts and each has its own branch below. */}
      {isLoading ? (
        // The shared Table's own skeleton, which replaced a hand-rolled one
        // that had no dark-mode classes at all.
        <Table<Policy> data={[]} columns={columns} isLoading rowKey={(policy) => policy.id} />
      ) : isError && policies.length === 0 ? (
        // A failed list request is NOT an empty book. Rendering the empty
        // state here told a broker "no policies match" when the request had
        // simply failed (e.g. a 400 on a filter the server refused).
        <Card padding="none">
          <div role="alert" className="px-6 py-12 text-center">
            <p className="text-sm font-medium text-red-700 dark:text-red-400">
              Could not load policies.
            </p>
            <button
              type="button"
              className="mt-3 text-sm text-primary-600 underline hover:no-underline dark:text-primary-400"
              onClick={() => void refetch()}
            >
              Try again
            </button>
          </div>
        </Card>
      ) : policies.length === 0 ? (
        <Card padding="none">
          <EmptyState />
        </Card>
      ) : (
        <>
          {/* U-02: the select-all control. It used to live in the header row;
              the shared Table's headers are text, so it moved here rather than
              keeping a hand-rolled <table> for one checkbox. Same label, same
              behaviour, and it is now visible without scrolling a wide table
              back to its first column. */}
          {canBulkSelect && (
            <div className="mb-3 flex items-center gap-2">
              <input
                id="policy-select-all"
                type="checkbox"
                aria-label="Select all policies on this page"
                checked={allOnPageSelected}
                onChange={toggleAllOnPage}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
              />
              <label htmlFor="policy-select-all" className="text-sm text-gray-600 dark:text-gray-400">
                Select all {visibleIds.length} on this page
              </label>
            </div>
          )}
          <Table<Policy>
            data={policies}
            columns={columns}
            rowKey={(policy) => policy.id}
            onRowClick={handleRowClick}
            // U-02: fixed widths (no column jump when the filters change) and
            // a header that stays put while the rows scroll under it.
            layout="fixed"
            stickyHeader
            // U-04: the Table never reorders anything -- it reports the next
            // sort state and the server does the ordering.
            sortKey={sortKey || undefined}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
          />
          {totalPages > 1 && (
            <Card padding="none" className="mt-4 overflow-hidden">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState() {
  const { user } = useAuthStore();
  const hasAddPolicyRight = useCan('policies', 'create');
  // Must match `canAddPolicy` above -- the empty state has its own copy, and
  // without the POSP-2 / CUSTOMER-8 roles here a POSP or customer with no
  // policies would still be offered "New Policy".
  const canAdd =
    user?.role !== 'country_admin' &&
    user?.role !== 'posp' &&
    user?.role !== 'super_posp' &&
    user?.role !== 'customer' &&
    !isInsurerRole(user?.role) &&
    hasAddPolicyRight;
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No policies found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {canAdd ? 'Get started by creating a new policy.' : 'No policies to display.'}
      </p>
      {canAdd && (
        <div className="mt-6">
          <Link to="/policies/new">
            <Button leftIcon={<PlusIcon className="w-5 h-5" />}>New Policy</Button>
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * Queue #235: a compact icon button for the Policies list Actions column.
 *
 * Icon-only so several actions fit in one cell without the row growing, with
 * `title` carrying the label for hover and `aria-label` for screen readers --
 * an unlabelled icon button is invisible to anyone not looking at it.
 */
function IconAction({
  title,
  onClick,
  busy,
  children,
}: {
  title: string;
  onClick: () => void;
  busy?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={busy}
      onClick={onClick}
      className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"
    >
      {busy ? (
        <span className="block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600 dark:border-gray-600 dark:border-t-primary-400" />
      ) : (
        children
      )}
    </button>
  );
}
